import { DELIVERY_TYPE } from "../../types";
import { OrderToSubmitRequest } from "../../types";
import { SiteResponse } from "../../types/site.type";

/**
 * Pure per-line (`ordersToSubmit`) transforms for the price/decrement +
 * delivery config step. Lifted verbatim from Order.tsx handler bodies; the hook
 * wires them to setState.
 */

export const assignSite = (
  orders: OrderToSubmitRequest[],
  lineId: string,
  site: SiteResponse | null
): OrderToSubmitRequest[] =>
  orders.map((mat: any) =>
    mat.id === lineId ? { ...mat, deliveryPlace: site } : mat
  );

/** Use-once address: keep the row "a convenir" (no deliveryPlace) but stash text. */
export const applyUseOnceAddress = (
  orders: OrderToSubmitRequest[],
  lineId: string,
  address: string
): OrderToSubmitRequest[] =>
  orders.map((mat: any) =>
    mat.id === lineId ? { ...mat, address, deliveryPlace: null } : mat
  );

export const applyDatesToAll = (
  orders: OrderToSubmitRequest[],
  source: any
): OrderToSubmitRequest[] =>
  orders.map((mat: any) => ({
    ...mat,
    spot: source.spot,
    startSupplyDate: source.startSupplyDate,
    endSupplyDate: source.endSupplyDate,
  }));

export const addLineFile = (
  orders: OrderToSubmitRequest[],
  lineId: string,
  file: any
): OrderToSubmitRequest[] =>
  orders.map((mat: any) =>
    mat.id === lineId ? { ...mat, files: [...(mat.files ?? []), file] } : mat
  );

export const removeLineFile = (
  orders: OrderToSubmitRequest[],
  lineId: string,
  index: number
): OrderToSubmitRequest[] =>
  orders.map((mat: any) =>
    mat.id === lineId
      ? {
          ...mat,
          files: (mat.files ?? []).filter((_: any, i: number) => i !== index),
        }
      : mat
  );

export const setLineField = (
  orders: OrderToSubmitRequest[],
  materialId: string,
  key: string,
  value: string
): OrderToSubmitRequest[] =>
  orders.map((mat: any) =>
    mat.id === materialId ? { ...mat, [key]: value } : mat
  );

/** Merge several fields into one line at once (the service card's edits, SCRUM-112). */
export const patchLine = (
  orders: OrderToSubmitRequest[],
  lineId: string,
  patch: Partial<OrderToSubmitRequest>
): OrderToSubmitRequest[] =>
  orders.map((mat: any) => (mat.id === lineId ? { ...mat, ...patch } : mat));

/** step-4 (PriceDecrementPayment) supply-period edits — "simple" variant. */
export const setSimpleSupplyPeriod = (
  orders: OrderToSubmitRequest[],
  matId: string,
  value: string,
  type: any
): OrderToSubmitRequest[] =>
  orders.map((mat: any) => {
    if (mat.id === matId && type === "start" && mat.spot === DELIVERY_TYPE.SPOT) {
      return { ...mat, startSupplyDate: value, endSupplyDate: value };
    } else if (mat.id === matId && type === "start") {
      return { ...mat, startSupplyDate: value, endSupplyDate: "" };
    } else if (mat.id === matId && type === "end") {
      return { ...mat, endSupplyDate: value };
    }
    if (mat.id === matId && type === DELIVERY_TYPE.SPOT) {
      return {
        ...mat,
        spot: value,
        startSupplyDate: mat.startSupplyDate,
        endSupplyDate: mat.startSupplyDate,
      };
    }
    return mat;
  });

/** step-5 (general data) supply-period edits — per-item variant. */
export const setSupplyPeriod = (
  orders: OrderToSubmitRequest[],
  matId: string,
  value: string,
  type: any
): OrderToSubmitRequest[] =>
  orders.map((mat: any) => {
    const isTarget = mat.id === matId;
    if (!isTarget) return mat;

    if (type === "start" && mat.spot === DELIVERY_TYPE.SPOT) {
      return { ...mat, startSupplyDate: value, endSupplyDate: value };
    }
    if (type === "start") {
      return { ...mat, startSupplyDate: value, endSupplyDate: "" };
    }
    if (type === "end") {
      return { ...mat, endSupplyDate: value };
    }
    if (type === DELIVERY_TYPE.SPOT || type === DELIVERY_TYPE.PERIOD) {
      return {
        ...mat,
        spot: value,
        endSupplyDate:
          value === DELIVERY_TYPE.SPOT ? mat.startSupplyDate : mat.endSupplyDate,
      };
    }
    return mat;
  });
